// File: src/pages/Home.jsx
// Description: Mahavihara University Home Page — cinematic photo-only banner

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  AcademicCapIcon,
  UserGroupIcon,
  UsersIcon,
  CalendarIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../context/LanguageContext';

const Home = () => {
  const { currentLanguage, getText, LANGUAGES } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);

  // ── Slideshow images (no title/subtitle needed — photo only) ──
  const slides = [
    { image: "/images/hero-bg.jpg" },
    { image: "/images/10.jpg" },
    { image: "/images/23.jpg" },
  ];

  // Auto-advance every 8 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 8000);
    return () => clearInterval(timer);
  }, [slides.length]);

  // Manual navigation
  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);

  // ── Stats ──
  const stats = [
    { label: { [LANGUAGES.EN]: 'Students',  [LANGUAGES.MM]: 'စာသင်သားသံဃာများ' }, value: "80+", icon: UsersIcon },
    { label: { [LANGUAGES.EN]: 'Teachers',  [LANGUAGES.MM]: 'ဆရာတော်များ'       }, value: "50+", icon: UserGroupIcon },
    { label: { [LANGUAGES.EN]: 'Courses',   [LANGUAGES.MM]: 'အတန်းများ'          }, value: 7,     icon: AcademicCapIcon },
    { label: { [LANGUAGES.EN]: 'Batches',   [LANGUAGES.MM]: 'သင်တန်းများ'        }, value: 12,    icon: CalendarIcon },
    { label: { [LANGUAGES.EN]: 'Graduates', [LANGUAGES.MM]: 'ဘွဲ့ရများ'          }, value: 950,   icon: CheckCircleIcon },
  ];

  // ── Notices ──
  const notices = [
    {
      date: '2025-05-01',
      title: {
        [LANGUAGES.EN]: '2024-25 Academic Year Class Promotion Examination Result',
        [LANGUAGES.MM]: '၂၀၂၄-၂၅ ပညာသင်နှစ် အတန်းတက်စာမေးပွဲ ရလဒ်'
      }
    },
    {
      date: '2025-04-28',
      title: {
        [LANGUAGES.EN]: 'New Semester Registration Open',
        [LANGUAGES.MM]: 'သင်တန်းသစ် စာရင်းသွင်းခြင်း ဖွင့်လှစ်'
      }
    },
    {
      date: '2025-02-10',
      title: {
        [LANGUAGES.EN]: '2024-25 Academic Year Final Examination Timetable',
        [LANGUAGES.MM]: '၂၀၂၄-၂၅ ပညာသင်နှစ် နောက်ဆုံးစာမေးပွဲ အချိန်ဇယား'
      }
    },
  ];

  // ── Activities ──
  const activities = [
    {
      date: '2025-06-16',
      title: {
        [LANGUAGES.EN]: 'First Day of 2024-2025 Academic Year Final Examination',
        [LANGUAGES.MM]: '၂၀၂၄-၂၅ ပညာသင်နှစ် နောက်ဆုံးစာမေးပွဲ ပထမနေ့'
      }
    },
    {
      date: '2025-03-11',
      title: {
        [LANGUAGES.EN]: 'Academic Year Final Examination begins',
        [LANGUAGES.MM]: 'ပညာသင်နှစ် နောက်ဆုံးစာမေးပွဲ စတင်'
      }
    },
    {
      date: '2025-02-24',
      title: {
        [LANGUAGES.EN]: 'Special Dhamma Talk',
        [LANGUAGES.MM]: 'အထူးဓမ္မဒေသနာ'
      }
    },
  ];

  // ── Quotes ──
  const quotes = {
    pali: {
      [LANGUAGES.EN]: 'Sīlaparidhotā paññā, paññāparidhotam Sīlam.',
      [LANGUAGES.MM]: 'သီလပရိဓောတာ ပညာ၊ ပညာ ပရိဓောတံ သီလံ'
    },
    wisdom: {
      [LANGUAGES.EN]: 'Wisdom is purified by morality, and morality is purified by wisdom.',
      [LANGUAGES.MM]: 'ပညာကိုသီလဖြင့်ထက်ဝန်းကျင်ဆေးကြောသုတ်သင်အပ်၏။ သီလကိုပညာဖြင့်ထက်ဝန်းကျင်ဆေးကြောသုတ်သင်အပ်၏။'
    },
    principles: {
      [LANGUAGES.EN]: 'The disciplinary rules (Sikkhāpada) from the Vinaya Pitaka must be thoroughly and completely observed. The university\'s guiding principle, "Live and Practice in Buddhist traditional Ways, learn and teach in Modern Methods"',
      [LANGUAGES.MM]: 'ဝိနည်းပိဋကတ်၌လာသောသိက္ခာပုဒ်တော်များကို ထက်ဝန်းကျင်ပြည့်စုံစွာ ဖြည့်ကျင့်ရမည်။ "နေနည်းရှေးကျ၊သင်နည်းခေတ်မီ"ဟူသောတက္ကသိုလ်၏လမ်းညွှန်မူဝါဒကိုအစဉ်ဂရုပြုရမည်။'
    }
  };

  return (
    <div className={currentLanguage === LANGUAGES.MM ? "myanmar-text" : ""}>

      {/* ══════════════════════════════════════
          CINEMATIC PHOTO BANNER — no text box
          ══════════════════════════════════════ */}
      <section className="hero-slideshow">

        {/* All slides stacked — CSS crossfade between them */}
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`slide-layer ${index === currentSlide ? 'slide-layer-active' : ''}`}
          >
            {/* Ken Burns zoom applied only to active slide */}
            <div
              className={`slide-background ${index === currentSlide ? 'slide-bg-zoom' : ''}`}
              style={{ backgroundImage: `url(${slide.image})` }}
            />
            {/* Bottom-heavy gradient for indicator readability */}
            <div className="slide-overlay" />
          </div>
        ))}

        {/* Ghost arrow navigation */}
        <button onClick={prevSlide} className="slide-nav slide-nav-prev" aria-label="Previous slide">
          <ChevronLeftIcon className="slide-nav-icon" />
        </button>
        <button onClick={nextSlide} className="slide-nav slide-nav-next" aria-label="Next slide">
          <ChevronRightIcon className="slide-nav-icon" />
        </button>

        {/* Slim progress-bar indicators */}
        <div className="slide-indicators">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`slide-indicator ${index === currentSlide ? 'active' : ''}`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* ══════════════════════════════════════
          UNIVERSITY MOTTO QUOTE
          ══════════════════════════════════════ */}
      <section className="quote-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="quote-content"
          >
            <ChatBubbleLeftRightIcon className="quote-icon" />
            <div className={`quote-pali ${currentLanguage === LANGUAGES.MM ? "myanmar-text" : ""}`}>
              {getText(quotes.pali)}
            </div>
            <div className={`quote-wisdom ${currentLanguage === LANGUAGES.MM ? "myanmar-text" : ""}`}>
              "{getText(quotes.wisdom)}"
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          STATS
          ══════════════════════════════════════ */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            {stats.map(({ label, value, icon: Icon }, index) => (
              <motion.div
                key={index}
                className="stat-card hover-lift"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Icon className="stat-icon" />
                <div className="stat-value">{value}</div>
                <div className={`stat-label ${currentLanguage === LANGUAGES.MM ? "myanmar-text" : ""}`}>
                  {getText(label)}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          UNIVERSITY PRINCIPLES
          ══════════════════════════════════════ */}
      <section className="principles-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="principles-content"
          >
            <h2 className={`principles-title ${currentLanguage === LANGUAGES.MM ? "myanmar-text" : ""}`}>
              {getText({
                [LANGUAGES.EN]: 'Our Guiding Principles',
                [LANGUAGES.MM]: 'ကျွန်ုပ်တို့၏ လမ်းညွှန်မူဝါဒများ'
              })}
            </h2>
            <div className="principles-card">
              <div className={`principles-text ${currentLanguage === LANGUAGES.MM ? "myanmar-text" : ""}`}>
                {getText(quotes.principles)}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          NOTICES & ACTIVITIES
          ══════════════════════════════════════ */}
      <section className="notices-activities-section">
        <div className="container">
          <div className="notices-activities-grid">

            {/* Notices */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="notices-container"
            >
              <h2 className={`section-title ${currentLanguage === LANGUAGES.MM ? "myanmar-text" : ""}`}>
                {getText({
                  [LANGUAGES.EN]: 'Notices & Information',
                  [LANGUAGES.MM]: 'ကြေညာချက် သတင်းအချက်အလက်များ'
                })}
              </h2>
              <div className="notices-list">
                {notices.map((notice, idx) => (
                  <div key={idx} className="notice-item hover-scale-small">
                    <div className="notice-date">{notice.date}</div>
                    <div className={`notice-title ${currentLanguage === LANGUAGES.MM ? "myanmar-text" : ""}`}>
                      {getText(notice.title)}
                    </div>
                  </div>
                ))}
              </div>
              <Link
                to="/notices"
                className={`section-link ${currentLanguage === LANGUAGES.MM ? "myanmar-text" : ""}`}
              >
                {getText({ [LANGUAGES.EN]: 'See all notices →', [LANGUAGES.MM]: 'ကြေညာချက်များအားလုံးကြည့်ရှုရန် →' })}
              </Link>
            </motion.div>

            {/* Activities */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="activities-container"
            >
              <h2 className={`section-title ${currentLanguage === LANGUAGES.MM ? "myanmar-text" : ""}`}>
                {getText({
                  [LANGUAGES.EN]: 'Current Activities',
                  [LANGUAGES.MM]: 'လက်ရှိလှုပ်ရှားမှုများ'
                })}
              </h2>
              <div className="activities-list">
                {activities.map((activity, idx) => (
                  <div key={idx} className="activity-item hover-scale-small">
                    <div className="activity-date">{activity.date}</div>
                    <div className={`activity-title ${currentLanguage === LANGUAGES.MM ? "myanmar-text" : ""}`}>
                      {getText(activity.title)}
                    </div>
                  </div>
                ))}
              </div>
              <Link
                to="/activities"
                className={`section-link ${currentLanguage === LANGUAGES.MM ? "myanmar-text" : ""}`}
              >
                {getText({ [LANGUAGES.EN]: 'See all activities →', [LANGUAGES.MM]: 'လှုပ်ရှားမှုများအားလုံးကြည့်ရှုရန် →' })}
              </Link>
            </motion.div>

          </div>
        </div>
      </section>

    </div>
  );
};

export default Home;